import { isSavedAwaitingConfirmation } from "@/lib/orderSubmissionOutcome";
import { sessionStore, isSessionExpired, SESSION_EXPIRED_MESSAGE } from "@/lib/session";
import { isListingExpired, isListingSold } from "@/lib/listingStatus";
import { analyticsRequestHeaders } from "@/lib/analytics";
import { isAgentAccount } from "@/lib/userTag";
import {
  isRecentSoldListing,
  listingArray,
  listingBelongsToOwner,
  mergeListingCollections,
  normalizeMyListingsResponse,
} from "@/lib/listingCollections";
import type {
  AppNotification,
  AssignedAgent,
  Auction,
  Bid,
  FarmerGroup,
  InventoryRecord,
  InvitationResponse,
  Listing,
  NotificationPage,
  OrderCustomer,
  OrderItem,
  OrderPayload,
  PickupLocation,
  SalesOrder,
  SalesPrice,
  ShipToAddress,
  User,
} from "@/types/domain";

const API_URL = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
const WEB_PUSH_SUBSCRIBE_PATH =
  (import.meta.env.VITE_WEB_PUSH_SUBSCRIBE_PATH || "").trim() ||
  "/saveWebPushSubscription";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const ORDER_SUBMIT_TIMEOUT_MS = 60_000;
const ORDER_STATUS_CHECK_TIMEOUT_MS = 15_000;
export const ORDER_TIMEOUT_MESSAGE = "TIMEOUT";
export const ORDER_NETWORK_MESSAGE =
  "Network error. Please check your internet connection.";

/**
 * True when we can't know whether the server received the request (timeout or
 * dropped connection), as opposed to an error the server actually returned.
 */
export const isUnconfirmedDeliveryError = (error: unknown): boolean =>
  error instanceof Error &&
  (error.message === ORDER_TIMEOUT_MESSAGE ||
    error.message === ORDER_NETWORK_MESSAGE);

export interface CreateOrderApiResponse {
  success: boolean;
  code?: string;
  data?: {
    orderId: string;
    bcSalesOrderNo?: string;
    status?: string;
    requiresReconciliation?: boolean;
  };
  message?: string;
}

type RequestOptions = RequestInit & {
  auth?: boolean;
  query?: Record<string, string | number | boolean | null | undefined>;
};

function getApiUrl(path: string, query?: RequestOptions["query"]): string {
  if (!API_URL) {
    throw new ApiError("VITE_API_URL is not configured.", 0);
  }

  const url = new URL(`${API_URL}${path.startsWith("/") ? path : `/${path}`}`);
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  });
  return url.toString();
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { auth = true, query, headers, body, ...init } = options;
  const token = sessionStore.getToken();
  if (auth && token && isSessionExpired(token)) {
    sessionStore.expire(token);
    throw new ApiError(SESSION_EXPIRED_MESSAGE, 401);
  }
  const isFormData = body instanceof FormData;

  const response = await fetch(getApiUrl(path, query), {
    ...init,
    body,
    headers: {
      ...analyticsRequestHeaders(),
      ...(isFormData ? {} : body ? { "Content-Type": "application/json" } : {}),
      ...(auth && token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
  });

  const contentType = response.headers.get("content-type") || "";
  const data: unknown = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    if (auth && token && response.status === 401) {
      sessionStore.expire(token);
      throw new ApiError(SESSION_EXPIRED_MESSAGE, 401, data);
    }
    const candidate = data as { message?: string; error?: string } | null;
    throw new ApiError(
      candidate?.message ||
        candidate?.error ||
        `Request failed (${response.status})`,
      response.status,
      data,
    );
  }

  return data as T;
}

function jsonBody(value: unknown): string {
  return JSON.stringify(value);
}

function asArray<T>(value: unknown, keys: string[]): T[] {
  if (Array.isArray(value)) return value as T[];
  if (!value || typeof value !== "object") return [];
  const record = value as Record<string, unknown>;
  for (const key of keys) {
    if (Array.isArray(record[key])) return record[key] as T[];
  }
  return [];
}

function normalizeUser(value: User): User {
  const imageUrl =
    value.userImage ||
    (typeof value.imageUrl === "string" ? value.imageUrl : "");
  return { ...value, userImage: imageUrl || undefined };
}

const SOLD_CARD_CACHE_MS = 30_000;
let soldCardCache:
  | { expiresAt: number; request: Promise<Listing[]> }
  | undefined;

function clearSoldCardCache() {
  soldCardCache = undefined;
}

async function loadSoldListingCards(): Promise<Listing[]> {
  if (soldCardCache && soldCardCache.expiresAt > Date.now()) {
    return soldCardCache.request;
  }

  const soldRequest = (async () => {
    const response = await request<unknown>("/getSoldListingDetails", {
      auth: false,
      cache: "no-store",
    });
    const summaries = listingArray(response, [
      "data",
      "listings",
      "soldListings",
      "items",
    ]).map((listing) => ({
      ...listing,
      sold: true,
      status: "sold",
      managementStatus: "sold",
    }));
    const details = await Promise.allSettled(
      summaries.map((listing) =>
        request<{ listing?: Listing; success?: boolean }>(
          "/getAppListingsById",
          {
            auth: false,
            cache: "no-store",
            query: { id: listing.listingId },
          },
        ).then((detailResponse) => ({
          ...listing,
          ...(detailResponse.listing || {}),
          sold: true,
          status: "sold",
          managementStatus: "sold",
        })),
      ),
    );

    return mergeListingCollections(
      summaries,
      details.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : [],
      ),
    );
  })();

  soldCardCache = {
    expiresAt: Date.now() + SOLD_CARD_CACHE_MS,
    request: soldRequest,
  };

  try {
    return await soldRequest;
  } catch (error) {
    clearSoldCardCache();
    throw error;
  }
}

function listingIsApproved(listing?: Listing): boolean {
  if (!listing) return false;
  const approved = String(listing.approved || "").trim().toLowerCase();
  const status = String(listing.status || "").trim().toLowerCase();
  return ["1", "yes", "true"].includes(approved) || status === "active";
}

function normalizeAuction(value: Auction): Auction {
  const productDetails =
    value.productDetails && typeof value.productDetails === "object"
      ? (value.productDetails as Record<string, unknown>)
      : {};
  const detailImages = Array.isArray(productDetails.images)
    ? (productDetails.images as string[])
    : [];
  const images = value.images?.length ? value.images : detailImages;
  return {
    ...value,
    title:
      value.title ||
      String(
        value.listingTitle ||
          productDetails.listingTitle ||
          "Carrfields livestock auction",
      ),
    description: value.description || String(productDetails.description || ""),
    images,
    imageUrl: value.imageUrl || images[0],
    startTime:
      value.startTime || String(value.startDateTime || value.startDate || ""),
    endTime: value.endTime || String(value.endDateTime || value.endDate || ""),
    bidCount: Number(value.bidCount ?? value.totalBids ?? 0),
    animalType: value.animalType || String(productDetails.animalType || ""),
    category: value.category || String(productDetails.animalType || ""),
    quantity:
      value.quantity ??
      (productDetails.quantity as string | number | undefined),
    breed: value.breed || String(productDetails.breed || ""),
    ageRange: value.ageRange || value.age || String(productDetails.age || ""),
    condition: value.condition || String(productDetails.condition || ""),
    region: value.region || String(productDetails.region || ""),
    deliveryDate:
      value.deliveryDate || String(productDetails.deliveryDate || ""),
    inspectionDate:
      value.inspectionDate || String(productDetails.inspectionDate || ""),
  };
}

function normalizeBid(value: Bid): Bid {
  return {
    ...value,
    amount: Number(value.amount ?? value.bidAmount ?? 0),
    userId: value.userId || String(value.bidderId || ""),
    userName: value.userName || String(value.bidderName || ""),
    createdAt: value.createdAt || String(value.timestamp || ""),
  };
}

function normalizeFarmerGroup(value: FarmerGroup): FarmerGroup {
  const members = Array.isArray(value.members) ? value.members : [];
  const farmersFromMembers: User[] = members.map((member) => {
    const name = String(member.farmerName || "").trim();
    const [firstName = "", ...lastNameParts] = name
      .split(/\s+/)
      .filter(Boolean);
    return {
      userId: String(member.farmerId || member.groupMemberId || ""),
      name,
      firstName,
      lastName: lastNameParts.join(" "),
      email: String(member.farmerEmail || ""),
      phone: String(member.farmerPhone || ""),
      region: String(member.farmerRegion || ""),
      farmType: member.farmType,
      userType: "farmer",
      groupMemberId: member.groupMemberId,
      addedAt: member.addedAt,
    };
  });
  const farmers =
    Array.isArray(value.farmers) && value.farmers.length > 0
      ? value.farmers
      : farmersFromMembers;
  return {
    ...value,
    farmers,
    members,
    memberCount: Number(
      value.memberCount ?? (members.length || farmers.length),
    ),
  };
}

async function fetchUsersPage(
  params: { limit?: number; lastKey?: unknown; search?: string } = {},
) {
  const response = await request<unknown>("/getUsers", {
    query: {
      limit: params.limit || 50,
      lastKey: params.lastKey
        ? typeof params.lastKey === "string"
          ? params.lastKey
          : JSON.stringify(params.lastKey)
        : undefined,
      search: params.search || undefined,
    },
  });
  const record =
    response && typeof response === "object"
      ? (response as { lastKey?: unknown; lastEvaluatedKey?: unknown })
      : {};
  return {
    users: asArray<User>(response, ["users", "items"]).map(normalizeUser),
    lastKey: record.lastKey || record.lastEvaluatedKey,
  };
}

/**
 * Walks every page of /getUsers and returns the full combined list. Shared
 * by api.users.list() and api.users.getAllAgents() so both go through a
 * single paginated bulk endpoint instead of one request per user.
 */
async function fetchAllUsers(params: { limit?: number } = {}): Promise<User[]> {
  const users: User[] = [];
  let cursor: unknown;
  let pages = 0;
  do {
    const page = await fetchUsersPage({ limit: params.limit || 100, lastKey: cursor });
    users.push(...page.users);
    cursor = page.lastKey;
    pages += 1;
  } while (cursor && pages < 50);
  return users;
}

export const api = {
  auth: {
    async login(email: string, password: string) {
      const response = await request<{
        success: boolean;
        token: string;
        user: User;
        message?: string;
      }>("/login", {
        method: "POST",
        auth: false,
        body: jsonBody({ email, password }),
      });
      return { ...response, user: normalizeUser(response.user) };
    },
    signup(payload: Record<string, unknown>) {
      const signupPayload = { ...payload };
      const tradingAccountNumber = signupPayload.tradingAccountNumber;
      delete signupPayload.confirmPassword;
      delete signupPayload.tradingAccountNumber;
      return request<{
        success: boolean;
        token?: string;
        user?: User;
        userId?: string;
        userType?: string;
        existingUser?: boolean;
        skipped?: boolean;
        message?: string;
      }>("/signup", {
        method: "POST",
        auth: false,
        body: jsonBody({
          ...signupPayload,
          carrfieldsAccountNumber:
            signupPayload.carrfieldsAccountNumber || tradingAccountNumber || "",
        }),
      });
    },
    forgotPassword(email: string, isBulkUpload = false) {
      return request<{
        message: string;
        email?: string;
        expiresIn?: string;
        // Only present when the backend has been updated to echo it back
        // (see forgotPassword.mjs) — lets the bulk-upload SMS queue text the
        // same create-password link that was emailed, without a second call.
        resetLink?: string;
      }>("/forgotPassword", {
        method: "POST",
        auth: false,
        body: jsonBody({ email, isBulkUpload }),
      });
    },
    resetPassword(email: string, resetToken: string, newPassword: string) {
      return request<{ success: boolean; message: string }>("/resetPassword", {
        method: "POST",
        auth: false,
        body: jsonBody({ email, resetToken, newPassword }),
      });
    },
    // Invitation links now only carry a token (see sendInvitation.mjs on
    // the backend) — this looks up everything else (invitee name/email/
    // phone, agent attribution) the signup page needs to prefill.
    getInvitationByToken(token: string) {
      return request<{
        success: boolean;
        message?: string;
        invitation?: {
          firstName?: string;
          lastName?: string;
          email?: string | null;
          phone?: string | null;
          agentId?: string;
          agentName?: string;
          agentEmail?: string;
          agentRegion?: string;
          agentAssign?: boolean;
        };
      }>("/verifyInvitations", {
        auth: false,
        query: { token },
      });
    },
  },

  listings: {
    async list(
      params: { animalType?: string; limit?: number; lastKey?: unknown } = {},
    ) {
      const [response, soldCards] = await Promise.all([
        request<{
        listings?: Listing[];
        hasMore?: boolean;
        lastKey?: unknown;
        totalCount?: number;
        }>("/getAppListings", {
          auth: false,
          query: {
            animalType: params.animalType,
            limit: params.limit || 18,
            lastKey: params.lastKey
              ? encodeURIComponent(JSON.stringify(params.lastKey))
              : undefined,
          },
        }),
        params.lastKey
          ? Promise.resolve([] as Listing[])
          : loadSoldListingCards().catch(() => []),
      ]);
      const category = String(params.animalType || "").trim().toLowerCase();
      const recentSoldCards = soldCards.filter(
        (listing) =>
          isRecentSoldListing(listing) &&
          (!category ||
            String(listing.animalType || "").trim().toLowerCase() === category),
      );
      const listings = mergeListingCollections(
        response.listings || [],
        recentSoldCards,
      ).filter((listing) => isListingSold(listing) || !isListingExpired(listing));
      return {
        listings,
        hasMore: Boolean(response.hasMore),
        lastKey: response.lastKey,
        totalCount: response.totalCount ?? listings.length,
      };
    },
    async get(listingId: string) {
      const response = await request<{ listing?: Listing; success?: boolean }>(
        "/getAppListingsById",
        { auth: false, query: { id: listingId } },
      );
      return response.listing || (response as unknown as Listing);
    },
    async mine(agentId: string, allAgents = false) {
      const [response, soldCards] = await Promise.all([
        request<unknown>("/myListings", {
          cache: "no-store",
          query: { agentId, ...(allAgents ? { scope: "all" } : {}) },
        }),
        loadSoldListingCards().catch(() => []),
      ]);
      const listings = normalizeMyListingsResponse(response);
      const ownedSoldCards = soldCards
        .filter((listing) => listingBelongsToOwner(listing, agentId))
        .map((listing) => ({ ...listing, managementStatus: "sold" }));
      return mergeListingCollections(listings, ownedSoldCards);
    },
    async sold() {
      const response = await request<unknown>("/getSoldListingDetails");
      return asArray<Listing & { soldPrice?: number; soldBy?: string }>(
        response,
        ["data", "listings", "soldListings", "body", "items"],
      );
    },
    async drafts(agentId?: string) {
      const response = await request<unknown>("/getDraftListing", {
        query: { agentId, limit: 100 },
      });
      return asArray<Listing>(response, ["listings", "drafts", "items"]);
    },
    create(payload: Record<string, unknown>) {
      return request<{
        success: boolean;
        listingId?: string;
        message?: string;
      }>("/addListings", {
        method: "POST",
        body: jsonBody(payload),
      });
    },
    async update(payload: Record<string, unknown>) {
      const response = await request<{
        success?: boolean;
        message?: string;
        listingId?: string;
        data?: Listing;
      }>(
        "/updateListings",
        {
          method: "POST",
          body: jsonBody(payload),
        },
      );
      if (
        payload.sold === true ||
        String(payload.status || "").trim().toLowerCase() === "sold"
      ) {
        clearSoldCardCache();
      }
      return response;
    },
    remove(listingId: string, replacementListingId?: string) {
      return request<{ success: boolean; message?: string }>(
        `/deleteListings/${encodeURIComponent(listingId)}`,
        {
          method: "DELETE",
          query: { replacementListingId },
        },
      );
    },
    async approve(listingId: string) {
      try {
        const response = await request<{
          success: boolean;
          message: string;
        }>("/approveListing", {
          method: "PUT",
          body: jsonBody({ listingId, approved: "1" }),
        });
        if (!response.success) {
          throw new ApiError(
            response.message || "Failed to approve listing.",
            200,
            response,
          );
        }
        return response;
      } catch (approvalError) {
        try {
          const verification = await request<{
            listing?: Listing;
            success?: boolean;
          }>("/getAppListingsById", {
            auth: false,
            cache: "no-store",
            query: { id: listingId, approvalCheck: Date.now() },
          });
          const savedListing =
            verification.listing || (verification as unknown as Listing);
          if (listingIsApproved(savedListing)) {
            return {
              success: true,
              message: "Listing approval confirmed.",
            };
          }
        } catch {
          // Preserve the original approval error when status cannot be verified.
        }
        throw approvalError;
      }
    },
    reject(
      listingId: string,
      reason: string,
      rejectedBy: string,
      rejectedByName: string,
    ) {
      return request<{ success: boolean; message: string }>("/rejectListing", {
        method: "PUT",
        body: jsonBody({
          listingId,
          rejectionReason: reason,
          rejectedBy,
          rejectedByName,
        }),
      });
    },
    presignedMedia(payload: Record<string, unknown>) {
      return request<Record<string, unknown>>("/uploadMedia", {
        method: "POST",
        body: jsonBody(payload),
      });
    },
  },

  watchlist: {
    async list(userId: string) {
      const response = await request<unknown>("/getUserFlag", {
        query: { userId },
      });
      const userFlags =
        response && typeof response === "object"
          ? (
              response as {
                userFlags?: Array<{
                  listingFlags?: Array<Listing & { flagWatchlist?: number }>;
                }>;
              }
            ).userFlags
          : undefined;
      return (userFlags?.[0]?.listingFlags || []).filter(
        (listing) => Number(listing.flagWatchlist) === 1,
      );
    },
    toggle(userId: string, listingId: string, flagWatchlist: 0 | 1) {
      return request<{
        success: boolean;
        isWatchlisted?: boolean;
        message?: string;
      }>("/getUserFlag", {
        method: "POST",
        body: jsonBody({ userId, listingId, flagWatchlist }),
      });
    },
  },

  profile: {
    async get(userId: string) {
      const response = await request<{ user?: User }>("/getUserById", {
        query: { userId },
      });
      return normalizeUser(response.user || (response as unknown as User));
    },
    update(payload: Record<string, unknown>) {
      return request<{
        success: boolean;
        user?: Partial<User>;
        message?: string;
      }>(
        "/updateProfile",
        {
          method: "PUT",
          body: jsonBody(payload),
        },
      );
    },
    getUploadUrl(userId: string, fileName: string, fileType: string) {
      return request<{ uploadUrl: string; fileUrl: string }>(
        "/getPresignedUrl",
        {
          method: "POST",
          body: jsonBody({ userId, fileName, fileType }),
        },
      );
    },
    updateImage(userId: string, imageUrl: string) {
      return request<{ success: boolean; user?: User }>("/updateUserImage", {
        method: "PUT",
        body: jsonBody({ userId, imageUrl }),
      });
    },
  },

  notifications: {
    async list(userId: string, cursor?: string) {
      const response = await request<unknown>("/getNotifications", {
        query: { recipientId: userId, cursor },
      });
      const record =
        response && typeof response === "object"
          ? (response as Record<string, unknown>)
          : {};
      const cursorValue = record.nextCursor ?? record.lastKey;
      return {
        notifications: asArray<AppNotification>(response, [
          "notifications",
          "items",
        ]),
        nextCursor: cursorValue ? String(cursorValue) : undefined,
        unreadCount: Number(record.unreadCount ?? 0),
      } satisfies NotificationPage;
    },
    markRead(notificationId: string, read: boolean) {
      return request<{ success: boolean }>(
        `/markAsRead/${encodeURIComponent(notificationId)}`,
        {
          method: "PUT",
          body: jsonBody({ read }),
        },
      );
    },
    remove(notificationId: string) {
      return request<{ success: boolean }>(
        `/deleteNotification/${encodeURIComponent(notificationId)}`,
        {
          method: "DELETE",
        },
      );
    },
    saveWebPushSubscription(payload: Record<string, unknown>) {
      return request<{ success: boolean; message?: string }>(
        WEB_PUSH_SUBSCRIBE_PATH,
        {
          method: "POST",
          body: jsonBody(payload),
        },
      );
    },
    webPushPublicKey() {
      return request<{ success: boolean; publicKey: string }>(
        "/webPushPublicKey",
        { auth: false },
      );
    },
    removeWebPushSubscription(payload: Record<string, unknown>) {
      return request<{ success: boolean; message?: string }>(
        WEB_PUSH_SUBSCRIBE_PATH,
        {
          method: "DELETE",
          body: jsonBody(payload),
        },
      );
    },
    requestAgentAssignment(payload: {
      farmerId: string;
      farmerName: string;
      farmerEmail: string;
      farmerPhone: string;
      farmerRegion: string;
      listingId: string;
      listingTitle?: string;
    }) {
      return request<{ success: boolean; adminsNotified: number }>(
        "/createAssignedAgentNotifications",
        { method: "PUT", body: jsonBody(payload) },
      );
    },
    inquiry(payload: Record<string, unknown>) {
      return request<{ success: boolean; message?: string }>(
        "/sendInquiryNotifications",
        {
          method: "POST",
          body: jsonBody(payload),
        },
      );
    },
    listingApproval(payload: Record<string, unknown>) {
      return request<{ success: boolean; message?: string }>(
        "/listingApproval",
        {
          method: "PUT",
          body: jsonBody(payload),
        },
      );
    },
  },

  users: {
    listPage: fetchUsersPage,
    list: fetchAllUsers,
    createAgent(payload: Record<string, unknown>) {
      return request<{
        success: boolean;
        temporaryPassword?: string;
        message?: string;
      }>("/signup", {
        method: "POST",
        body: jsonBody(payload),
      });
    },
    update(userId: string, payload: Record<string, unknown>) {
      return request<{ success: boolean; user?: User }>(
        `/admin/updateUser/${encodeURIComponent(userId)}`,
        {
          method: "PUT",
          body: jsonBody(payload),
        },
      );
    },
    remove(userId: string) {
      return request<{ success: boolean }>(
        `/admin/deleteUser/${encodeURIComponent(userId)}`,
        {
          method: "DELETE",
        },
      );
    },
    sendInvitation(payload: Record<string, unknown>) {
      return request<InvitationResponse>("/sendInvitations", {
        method: "POST",
        body: jsonBody(payload),
      });
    },
    async agentsByRegion(region: string) {
      const response = await request<unknown>("/getAgentsByRegion", {
        query: { region },
      });
      return asArray<User>(response, ["agents", "users", "items"]);
    },
    async getById(userId: string) {
      const response = await request<{ success: boolean; user?: User }>(
        "/getUserById",
        { query: { userId } },
      );
      return response.user || null;
    },
    /**
     * Full agent profiles (name, region, etc.) come from the paginated
     * /getUsers listing, filtered down to agent accounts. This avoids the
     * old N+1 pattern of one getUserById call per agent (100+ sequential
     * round trips), which made the listing editor's agent picker take
     * several seconds — or longer under throttling — to populate.
     */
    async getAllAgents() {
      const users = await fetchAllUsers({ limit: 100 });
      return users.filter(isAgentAccount);
    },
    assignAgent(payload: Record<string, unknown>) {
      return request<{
        success: boolean;
        message?: string;
        assignedAgentDetails: AssignedAgent[];
        assignedCount: number;
      }>("/assignAgent", {
        method: "POST",
        body: jsonBody(payload),
      });
    },
    bulkUpload(payload: Record<string, unknown>) {
      return request<Record<string, unknown>>("/bulkUploadFarmers", {
        method: "POST",
        body: jsonBody(payload),
      });
    },
  },

  groups: {
    async forAgent(agentId: string) {
      const response = await request<unknown>("/getAgentGroups", {
        query: { agentId },
      });
      return asArray<FarmerGroup>(response, ["groups", "items"]).map(
        normalizeFarmerGroup,
      );
    },
    async forFarmer(farmerId: string) {
      const response = await request<unknown>(
        `/getFarmerGroups/${encodeURIComponent(farmerId)}`,
      );
      return asArray<FarmerGroup>(response, ["groups", "items"]).map(
        normalizeFarmerGroup,
      );
    },
    async get(groupId: string) {
      const response = await request<{ group?: FarmerGroup }>(
        `/getGroupDetails/${encodeURIComponent(groupId)}`,
      );
      return normalizeFarmerGroup(
        response.group || (response as unknown as FarmerGroup),
      );
    },
    async agentFarmers(agentId: string) {
      const response = await request<unknown>("/getAgentFarmers", {
        query: { agentId },
      });
      return asArray<User>(response, ["farmers", "users", "items"]);
    },
    create(payload: Record<string, unknown>) {
      return request<{ success: boolean; group?: FarmerGroup }>(
        "/createFarmerGroup",
        {
          method: "POST",
          body: jsonBody(payload),
        },
      );
    },
    addFarmers(payload: Record<string, unknown>) {
      return request<{
        success: boolean;
        addedCount?: number;
        skippedCount?: number;
      }>("/addFarmersToGroup", {
        method: "POST",
        body: jsonBody(payload),
      });
    },
    removeFarmer(payload: Record<string, unknown>) {
      return request<{ success: boolean }>("/removeFarmerFromGroup", {
        method: "DELETE",
        body: jsonBody(payload),
      });
    },
    shareListing(payload: Record<string, unknown>) {
      return request<{ success: boolean }>("/shareListingToGroups", {
        method: "POST",
        body: jsonBody(payload),
      });
    },
    remove(groupId: string, deletedBy: string) {
      return request<{ success: boolean }>("/deleteGroup", {
        method: "DELETE",
        body: jsonBody({ groupId, deletedBy }),
      });
    },
  },

  auctions: {
    async list() {
      const response = await request<unknown>("/auctions", { auth: false });
      return asArray<Auction>(response, ["auctions", "items"]).map(
        normalizeAuction,
      );
    },
    async get(auctionId: string) {
      const response = await request<{ auction?: Auction }>(
        `/auctions/${encodeURIComponent(auctionId)}`,
        { auth: false },
      );
      return normalizeAuction(
        response.auction || (response as unknown as Auction),
      );
    },
    create(payload: Record<string, unknown>) {
      return request<{ success: boolean; auction?: Auction }>("/auctions", {
        method: "POST",
        body: jsonBody(payload),
      });
    },
    update(auctionId: string, payload: Record<string, unknown>) {
      return request<{ success: boolean }>(
        `/auctions/${encodeURIComponent(auctionId)}`,
        {
          method: "PUT",
          body: jsonBody(payload),
        },
      );
    },
    remove(auctionId: string, userId: string, userType: string) {
      return request<{ success: boolean }>(
        `/auctions/${encodeURIComponent(auctionId)}`,
        {
          method: "DELETE",
          body: jsonBody({ userId, userType, action: "delete" }),
        },
      );
    },
    cancel(auctionId: string, userId: string, userType: string) {
      return request<{ success: boolean }>(
        `/auctions/${encodeURIComponent(auctionId)}`,
        {
          method: "DELETE",
          body: jsonBody({ action: "cancel", userId, userType }),
        },
      );
    },
    placeBid(auctionId: string, payload: Record<string, unknown>) {
      return request<{ success: boolean; bid?: Bid; message?: string }>(
        `/auctions/${encodeURIComponent(auctionId)}/bids`,
        {
          method: "POST",
          body: jsonBody(payload),
        },
      );
    },
    autoBid(auctionId: string, payload: Record<string, unknown>) {
      return request<{ success: boolean }>(
        `/auctions/${encodeURIComponent(auctionId)}/auto-bid`,
        {
          method: "POST",
          body: jsonBody(payload),
        },
      );
    },
    async bids(auctionId: string) {
      const response = await request<unknown>(
        `/auctions/${encodeURIComponent(auctionId)}/bids`,
        { auth: false },
      );
      const direct = asArray<Bid>(response, ["bids", "items"]);
      const auction =
        response && typeof response === "object"
          ? (response as { auction?: { bids?: Bid[] } }).auction
          : undefined;
      return (direct.length ? direct : auction?.bids || []).map(normalizeBid);
    },
    cancelBid(auctionId: string, payload: Record<string, unknown>) {
      return request<{ success: boolean }>(
        `/auctions/${encodeURIComponent(auctionId)}/bids/cancel`,
        {
          method: "POST",
          body: jsonBody(payload),
        },
      );
    },
  },

  ordering: {
    async customers(
      search = "",
      lastKey?: unknown,
      signal?: AbortSignal,
    ) {
      return request<{
        customers: OrderCustomer[];
        hasMore?: boolean;
        lastKey?: unknown;
      }>("/getCustomers", {
        signal,
        query: {
          customerNoPrefix: search && /^\d+$/.test(search) ? search : undefined,
          namePrefix: search && !/^\d+$/.test(search) ? search : undefined,
          lastKey: lastKey
            ? encodeURIComponent(JSON.stringify(lastKey))
            : undefined,
        },
      });
    },
    async items(search = "", lastKey?: unknown, signal?: AbortSignal) {
      return request<{
        items: OrderItem[];
        hasMore?: boolean;
        lastKey?: unknown;
      }>("/getItems", {
        signal,
        query: {
          itemNoPrefix: search && /^\d/.test(search) ? search : undefined,
          descriptionPrefix: search && !/^\d/.test(search) ? search : undefined,
          lastKey: lastKey
            ? encodeURIComponent(JSON.stringify(lastKey))
            : undefined,
        },
      });
    },
    async inventory(
      locationCode = "",
      lastKey?: unknown,
      signal?: AbortSignal,
    ) {
      const response = await request<{
        data?: InventoryRecord[];
        hasMore?: boolean;
        lastKey?: unknown;
      }>("/getItemsAvailability", {
        signal,
        query: {
          includeItemDetails: true,
          locationCode,
          lastKey: lastKey
            ? encodeURIComponent(JSON.stringify(lastKey))
            : undefined,
        },
      });
      return { ...response, data: response.data || [] };
    },
    async prices(itemNumbers: string[], signal?: AbortSignal) {
      const response = await request<unknown>("/getSalesPrice/batch", {
        auth: false,
        signal,
        query: { itemNumbers: itemNumbers.join(",") },
      });
      return asArray<SalesPrice>(response, [
        "prices",
        "salesPrices",
        "items",
      ]).map((price) => ({
        ...price,
        sales_type: String(price.sales_type || price.salesType || ""),
        price_type: String(price.price_type || price.priceType || ""),
        unit_price: Number(price.unit_price ?? price.unitPrice ?? 0),
        published_price: Number(
          price.published_price ??
            price.publishedPrice ??
            price.unit_price ??
            price.unitPrice ??
            0,
        ),
        minimum_quantity: Number(
          price.minimum_quantity ?? price.minimumQuantity ?? 0,
        ),
        starting_date: String(price.starting_date || price.startingDate || ""),
        ending_date: String(price.ending_date || price.endingDate || ""),
        unit_of_measure_code: String(
          price.unit_of_measure_code || price.unitOfMeasureCode || "",
        ),
      }));
    },
    async addresses(customerNo: string) {
      const response = await request<unknown>(
        `/customers/${encodeURIComponent(customerNo)}/shipToAddress`,
      );
      return asArray<ShipToAddress & { post_code?: string }>(response, [
        "addresses",
        "shipToAddresses",
        "items",
      ]).map((address) => ({
        ...address,
        postcode: address.postcode || address.post_code,
      }));
    },
    async allAddresses(signal?: AbortSignal) {
      const response = await request<{
        customers: Array<
          OrderCustomer & {
            shipToAddresses?: Array<ShipToAddress & { post_code?: string }>;
          }
        >;
      }>("/customers-with-addresses", { signal });
      return {
        ...response,
        customers: (response.customers || []).map((customer) => ({
          ...customer,
          shipToAddresses: (customer.shipToAddresses || []).map((address) => ({
            ...address,
            postcode: address.postcode || address.post_code,
          })),
        })),
      };
    },
    async orders(customerId?: string, signal?: AbortSignal) {
      const response = await request<unknown>("/orders/history", {
        signal,
        query: { customerId },
      });
      return asArray<SalesOrder>(response, ["orders", "items"]);
    },
    async order(orderId: string) {
      const response = await request<{ order?: SalesOrder }>(
        `/orders/${encodeURIComponent(orderId)}`,
      );
      return response.order || (response as unknown as SalesOrder);
    },
    async createOrder(payload: OrderPayload): Promise<CreateOrderApiResponse> {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort(),
        ORDER_SUBMIT_TIMEOUT_MS,
      );
      try {
        return await request<CreateOrderApiResponse>("/orders/create", {
          method: "POST",
          body: jsonBody(payload),
          signal: controller.signal,
        });
      } catch (error) {
        // A held receipt for this exact order means it is saved server-side.
        if (
          error instanceof ApiError &&
          error.status === 409 &&
          isSavedAwaitingConfirmation(error.details, payload.orderId)
        ) {
          return error.details as CreateOrderApiResponse;
        }
        if (error instanceof DOMException && error.name === "AbortError")
          throw new Error(ORDER_TIMEOUT_MESSAGE, { cause: error });
        if (error instanceof TypeError) throw new Error(ORDER_NETWORK_MESSAGE, { cause: error });
        throw error;
      } finally {
        window.clearTimeout(timeoutId);
      }
    },
    /**
     * Checks whether an order with this client-generated ID already exists.
     * Only a 404 means "confirmed not submitted". A 403 or any other failure
     * throws so the caller never risks a duplicate create.
     */
    async orderStatus(orderId: string): Promise<{
      exists: boolean;
      order?: { orderId: string; status?: string; bcSalesOrderNo?: string | null };
    }> {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort(),
        ORDER_STATUS_CHECK_TIMEOUT_MS,
      );
      try {
        const response = await request<{
          order?: { orderId: string; status?: string; bcSalesOrderNo?: string | null };
        }>(`/orders/${encodeURIComponent(orderId)}`, {
          signal: controller.signal,
        });
        return { exists: true, order: response.order };
      } catch (error) {
        if (error instanceof ApiError && error.status === 404)
          return { exists: false };
        if (error instanceof DOMException && error.name === "AbortError")
          throw new Error(ORDER_TIMEOUT_MESSAGE, { cause: error });
        if (error instanceof TypeError) throw new Error(ORDER_NETWORK_MESSAGE, { cause: error });
        throw error;
      } finally {
        window.clearTimeout(timeoutId);
      }
    },
    async retry(orderId: string) {
      const response = await request<{ order?: SalesOrder }>(
        `/orders/${encodeURIComponent(orderId)}`,
      );
      const order = response.order || (response as unknown as SalesOrder);
      return request<{ success: boolean; message?: string }>("/orders/create", {
        method: "POST",
        body: jsonBody({
          retryOrderId: orderId,
          customer: {
            customerId: order.customerId,
            customerName: order.customerName,
          },
          delivery: {
            deliveryType: order.deliveryType,
            dispatchMethod: order.dispatchMethod,
            shipToName: order.shipToName,
            shipToAddress: order.shipToAddress,
            shipToCity: order.shipToCity,
            shipToPostcode: order.shipToPostcode,
            deliveryInstructions: order.deliveryInstructions,
            orderMixingInstructions: order.orderMixingInstructions,
            requestedDeliveryDate: order.requestedDeliveryDate,
          },
          lines: (order.lines || []).map((line) => ({
            itemNo: line.itemNo,
            description: line.description,
            quantity: line.quantity,
            unitPrice: line.unitPrice,
            unitOfMeasureCode: line.unitOfMeasureCode || "EACH",
            locationCode: line.locationCode,
            mixTreatInstructions: line.mixingInstructions,
            isDG: line.isDG,
            isBackOrder: line.isBackOrder,
          })),
          totals: {
            subtotalExclGst: order.subtotalExclGst,
            gstAmount: order.gstAmount,
            totalInclGst: order.totalInclGst,
          },
          createdByUserId: order.createdByUserId,
          createdByUserEmail: order.createdByUserEmail,
        }),
      });
    },
    cancel(orderId: string) {
      return request<{ success: boolean; message?: string }>(
        `/orders/${encodeURIComponent(orderId)}`,
        {
          method: "DELETE",
        },
      );
    },
    async locations(signal?: AbortSignal) {
      const response = await request<unknown>("/getLocations", { signal });
      return asArray<PickupLocation>(response, ["locations", "items"]);
    },
  },

  reports: {
    agentFarmer() {
      return request<{
        success: boolean;
        csvData: string;
        fileName: string;
        recordCount?: number;
        agentCount?: number;
        farmerCount?: number;
        reportData?: Array<{
          agentName?: string;
          agentEmail?: string;
          farmerFirstName?: string;
          farmerLastName?: string;
          farmerEmail?: string;
          farmerRegion?: string;
        }>;
      }>("/generateAgentsFarmerReport");
    },
  },
};

export async function uploadToPresignedUrl(
  url: string,
  file: File,
): Promise<void> {
  const response = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!response.ok) throw new ApiError("File upload failed.", response.status);
}
